import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { routingApi } from "@/lib/sprint2Entities";
import { apiError } from "@/lib/errors";
import { FutureCapabilityModal } from "@/components/FutureCapabilityModal";
import { FutureCapability } from "@/lib/sprint2Entities";
import { MapPin, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";

const TABS = [
  { k: "paste", label: "Paste" },
  { k: "pdf", label: "Upload PDF" },
  { k: "excel", label: "Upload Excel/CSV" },
];

function humanizeWarning(w) {
  if (typeof w !== "string") return String(w);
  if (w.startsWith("orphan_date_dropped:")) {
    const m = w.match(/(\d{4}-\d{2}-\d{2})/);
    return `A date${m ? ` (${m[1]})` : ""} was found without a following location line — dropped from the itinerary. Add the missing city below if needed.`;
  }
  if (w.startsWith("unresolved_legs:")) {
    const n = Number((w.split(":")[1] || "").trim());
    return `${n} leg${n === 1 ? "" : "s"} could not be geocoded. Edit the city/state or enter Manual Miles on the affected legs.`;
  }
  return w;
}

export default function RoutingTab({ tourId, canWrite = true }) {
  const [active, setActive] = useState("paste");
  const [text, setText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [futureOpen, setFutureOpen] = useState(false);

  const load = async () => {
    try { setDrafts(await routingApi.drafts(tourId)); }
    catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tourId]);

  const useSample = async () => {
    try {
      const s = await routingApi.sample();
      setText(s.sample_text);
    } catch (e) { toast.error(apiError(e)); }
  };

  const analyze = async () => {
    if (!text.trim()) return toast.error("Paste routing text or use the sample");
    setAnalyzing(true);
    await new Promise((r) => setTimeout(r, 1200));  // demo pacing
    try {
      await routingApi.analyze(tourId, { source: "paste", raw_text: text });
      toast.success("Routing analyzed");
      setText("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setAnalyzing(false); }
  };

  const analyzeDemoUpload = async (source) => {
    setAnalyzing(true);
    await new Promise((r) => setTimeout(r, 1400));
    try {
      await routingApi.analyze(tourId, { source, filename: "sample-routing.pdf", _use_sample: true });
      toast.success("Sample routing extracted (demo mode)");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setAnalyzing(false); }
  };

  const promote = async (draftId) => {
    try {
      const res = await routingApi.promote(draftId);
      const count = Number(res?.promoted ?? 0);
      const stopLabel = count === 1 ? "stop" : "stops";
      if (res?.tour_mileage_updated) {
        toast.success(`Itinerary saved — ${count} ${stopLabel}. Tour mileage updated.`);
      } else if (Number(res?.unresolved_leg_count) > 0) {
        toast.success(`Itinerary saved — ${count} ${stopLabel}. Tour mileage NOT updated (unresolved legs remain).`);
      } else {
        toast.success(`Itinerary saved — ${count} ${stopLabel}.`);
      }
      load();
    } catch (e) {
      // Log the technical detail for developers; show a friendly line to
      // the operator with zero backend/API terminology.
      // eslint-disable-next-line no-console
      console.warn("save_itinerary failed:", apiError(e));
      toast.error("Itinerary could not be saved. Please review the highlighted stops and try again.");
    }
  };

  // F4 · render mileage honestly. Statuses per leg are:
  //   estimated  → great-circle × 1.15 offline calculation
  //   manual     → operator override
  //   unresolved → at least one endpoint city could not be geocoded
  // The overall total is only trusted when zero legs are unresolved;
  // otherwise we show "Known Mileage" + unresolved-leg count and route
  // the operator to fix the affected rows.
  const scrollToWarnings = (draftId) => {
    const el = document.querySelector(`[data-testid="routing-legs-${draftId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const mileageDisplay = (d) => {
    const stopCount = d.parsed_stops?.length || 0;
    const miles = Number(d.estimated_mileage || 0);
    const hours = Number(d.estimated_drive_hours || 0);
    const legs = d.leg_mileage || [];
    const unresolved = legs.filter((l) => (l?.status || (l?.miles == null && l?.manual_miles == null ? "unresolved" : "estimated")) === "unresolved").length;
    if (stopCount < 2 || legs.length === 0) return { text: "No legs to route yet", unresolved: false, unresolvedCount: 0 };
    if (unresolved > 0 && miles > 0) {
      return {
        text: `Known Mileage: ${miles.toLocaleString()} mi · ${unresolved} unresolved leg${unresolved === 1 ? "" : "s"}`,
        unresolved: true,
        unresolvedCount: unresolved,
      };
    }
    if (unresolved > 0) {
      return {
        text: `Mileage incomplete — ${unresolved} unresolved leg${unresolved === 1 ? "" : "s"}`,
        unresolved: true,
        unresolvedCount: unresolved,
      };
    }
    return {
      text: `Route Mileage: ${miles.toLocaleString()} mi estimated · ~${hours}h drive`,
      unresolved: false,
      unresolvedCount: 0,
    };
  };

  return (
    <div className="space-y-4" data-testid="routing-tab">
      <div className="bg-white border border-coach-silver rounded-md">
        <div className="flex border-b border-coach-silver">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setActive(t.k)}
              className={`px-4 py-2 text-[12.5px] uppercase tracking-widest ${active === t.k ? "text-coach-blue border-b-2 border-coach-blue -mb-px" : "text-steel hover:text-carbon"}`}
              data-testid={`routing-tab-${t.k}`}>
              {t.label}
            </button>
          ))}
        </div>

        {active === "paste" && (
          <div className="p-4 space-y-3">
            <textarea rows={7} value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Paste routing text — the AI will extract dates, cities, venues, and compute mileage."
              className="w-full border border-coach-silver rounded-sm px-3 py-2 text-[13px] font-mono"
              data-testid="routing-paste-text" />
            <div className="flex items-center gap-2">
              <button onClick={useSample} className="text-coach-blue hover:underline text-[12.5px]"
                data-testid="routing-use-sample">Use sample text</button>
              <div className="flex-1" />
              <button onClick={analyze} disabled={analyzing || !canWrite}
                className="bg-coach-blue text-white text-[12.5px] font-medium px-4 py-2 rounded-sm disabled:opacity-40"
                data-testid="routing-analyze">
                {analyzing ? "Analyzing routing…" : "Analyze with AI"}
              </button>
            </div>
            <div className="text-[11px] text-steel">
              AI-Assisted routing draft — deterministic parser. Edit any stop below after Analyze.
            </div>
          </div>
        )}

        {active === "pdf" && (
          <div className="p-4">
            <div className="border-2 border-dashed border-coach-silver rounded-md p-8 text-center">
              <div className="text-[13px] text-carbon">Drop a PDF here, or</div>
              <button onClick={() => analyzeDemoUpload("pdf")} disabled={analyzing || !canWrite}
                className="mt-2 bg-coach-blue text-white text-[12.5px] font-medium px-4 py-2 rounded-sm disabled:opacity-40"
                data-testid="routing-upload-pdf">
                {analyzing ? "Extracting…" : "Load sample-routing.pdf (demo)"}
              </button>
              <div className="text-[11px] text-steel mt-3">
                Real PDF uploads with filenames not containing "demo" or "sample" are saved for staff review.
                <button onClick={() => setFutureOpen(true)} className="text-coach-blue hover:underline ml-1"
                  data-testid="routing-pdf-future">Learn more</button>
              </div>
            </div>
          </div>
        )}

        {active === "excel" && (
          <div className="p-4">
            <div className="border-2 border-dashed border-coach-silver rounded-md p-8 text-center">
              <div className="text-[13px] text-carbon">Drop an Excel or CSV file, or</div>
              <button onClick={() => analyzeDemoUpload("excel")} disabled={analyzing || !canWrite}
                className="mt-2 bg-coach-blue text-white text-[12.5px] font-medium px-4 py-2 rounded-sm disabled:opacity-40"
                data-testid="routing-upload-excel">
                {analyzing ? "Extracting…" : "Load sample-routing.xlsx (demo)"}
              </button>
              <div className="text-[11px] text-steel mt-3">
                <button onClick={() => setFutureOpen(true)} className="text-coach-blue hover:underline"
                  data-testid="routing-excel-future">Production upload capability →</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3 min-w-0">
        {drafts.map((d) => {
          const isOpen = expanded[d.id];
          return (
            <div key={d.id} className="bg-white border border-coach-silver rounded-md min-w-0 overflow-hidden" data-testid={`routing-draft-${d.id}`}>
              <div className="flex items-center gap-3 p-3 border-b border-coach-silver/60">
                <MapPin size={16} className="text-coach-blue" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-carbon" data-testid={`routing-summary-${d.id}`}>
                    <strong data-testid={`routing-stops-count-${d.id}`}>{d.stops_count ?? (d.parsed_stops || []).filter((s) => s.sequence != null).length} stops</strong>
                    {(() => {
                      const m = mileageDisplay(d);
                      if (m.unresolved) {
                        return (
                          <>
                            {" · "}
                            <span className="text-amber-hold"
                                  data-testid={`routing-mileage-unresolved-${d.id}`}>
                              {m.text}
                            </span>
                            <button type="button"
                                    onClick={() => { setExpanded({ ...expanded, [d.id]: true }); setTimeout(() => scrollToWarnings(d.id), 60); }}
                                    className="ml-2 text-coach-blue underline text-[11.5px]"
                                    data-testid={`routing-review-legs-${d.id}`}>
                              Review
                            </button>
                          </>
                        );
                      }
                      return <> · <span data-testid={`routing-mileage-${d.id}`}>{m.text}</span></>;
                    })()}
                    {d.status === "reviewed" && (
                      <span className="ml-2 inline-block align-middle bg-touring-lime/30 border border-touring-lime text-carbon text-[10.5px] uppercase tracking-widest px-1.5 py-[1px] rounded-sm"
                            data-testid={`routing-saved-badge-${d.id}`}>
                        Itinerary Saved
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-steel">
                    {d.source} · {d.created_at?.replace("T", " ").slice(0, 16)} · status: {d.status}
                  </div>
                </div>
                {d.status === "draft" && canWrite && (
                  <button onClick={() => promote(d.id)} className="border border-coach-blue text-coach-blue text-[12px] px-2 py-1 rounded-sm"
                    data-testid={`routing-save-itinerary-${d.id}`}>Save Itinerary</button>
                )}
                <button onClick={() => setExpanded({ ...expanded, [d.id]: !isOpen })}
                  className="text-steel" data-testid={`routing-expand-${d.id}`}>
                  {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
              </div>
              {isOpen && (
                <div className="p-4 space-y-4">
                  <ReviewStopsEditor draft={d} canWrite={canWrite && d.status === "draft"}
                                     onSaved={load} />
                  <LegMileagePanel draft={d} canWrite={canWrite && d.status === "draft"}
                                   onChanged={load} />
                  <CoDriverPanel draft={d} />
                  {(d.import_notes || []).length > 0 && (
                    <div className="mt-4 border border-coach-silver bg-road-white rounded-sm p-3"
                         data-testid={`routing-notes-${d.id}`}>
                      <div className="text-[11.5px] uppercase tracking-widest text-steel font-medium mb-1">
                        Notes ({(d.import_notes || []).length})
                      </div>
                      <ul className="text-[12.5px] text-carbon list-disc pl-4">
                        {d.import_notes.map((n, i) => (
                          <li key={i} data-testid={`routing-note-${d.id}-${i}`}>{n}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(d.ignored_lines || []).length > 0 && (
                    <div className="mt-3 border border-coach-silver bg-white rounded-sm p-3"
                         data-testid={`routing-ignored-${d.id}`}>
                      <div className="text-[11.5px] uppercase tracking-widest text-steel font-medium mb-1">
                        Ignored (not a stop)
                      </div>
                      <ul className="text-[12.5px] text-steel list-disc pl-4">
                        {d.ignored_lines.map((n, i) => (
                          <li key={i} data-testid={`routing-ignored-${d.id}-${i}`}>{n}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {d.warnings?.length > 0 && (
                    <div className="mt-4 border border-amber-hold bg-amber-hold/10 rounded-sm p-3"
                         data-testid={`routing-warnings-${d.id}`}>
                      <div className="text-[11.5px] uppercase tracking-widest text-amber-hold font-medium mb-1 flex items-center gap-1">
                        <AlertTriangle size={12} /> Review notices ({d.warnings.length})
                      </div>
                      <ul className="text-[12.5px] text-carbon list-disc pl-4">
                        {d.warnings.map((w, i) => (
                          <li key={i} data-testid={`routing-warning-${d.id}-${i}`}>{humanizeWarning(w)}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {drafts.length === 0 && (
          <div className="bg-white border border-coach-silver rounded-md p-6 text-center text-steel text-[13px]">
            No routing drafts yet.
          </div>
        )}
      </div>

      <FutureCapabilityModal open={futureOpen} onOpenChange={setFutureOpen}
        title="AI Extraction — Production Capability"
        body={FutureCapability.aiExtraction} />
    </div>
  );
}

function ReviewStopsEditor({ draft, canWrite, onSaved }) {
  const [rows, setRows] = useState(() => (draft.parsed_stops || []).map((s) => ({ ...s })));
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setRows((draft.parsed_stops || []).map((s) => ({ ...s })));
    setDirty(false);
  }, [draft.id, draft.updated_at]);

  const setRow = (i, patch) => {
    const next = rows.map((r, j) => (j === i ? { ...r, ...patch } : r));
    setRows(next);
    setDirty(true);
  };
  const addRow = () => { setRows([...rows, { sequence: rows.length + 1, stop_date: null, city: "", state: "", venue: null, notes: null }]); setDirty(true); };
  const removeRow = (i) => { setRows(rows.filter((_, j) => j !== i)); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r, i) => ({
        sequence: i + 1,
        stop_date: r.stop_date || null,
        city: (r.city || "").trim() || null,
        state: (r.state || "").trim().toUpperCase().slice(0, 2) || null,
        venue: r.venue || null,
        notes: r.notes || null,
      }));
      await routingApi.patch(draft.id, { parsed_stops: payload });
      toast.success("Stops saved");
      setDirty(false);
      onSaved?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("routing_patch failed:", apiError(e));
      toast.error("Changes could not be saved. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={`routing-review-${draft.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11.5px] uppercase tracking-widest text-steel">Review Stops</div>
        {canWrite && (
          <div className="flex gap-2">
            <button onClick={addRow} className="text-coach-blue text-[12px] hover:underline"
                    data-testid={`routing-add-stop-${draft.id}`}>+ Add Stop</button>
            <button onClick={save} disabled={!dirty || saving}
                    className={`text-[12px] px-2 py-1 rounded-sm border ${dirty ? "border-coach-blue text-coach-blue" : "border-coach-silver text-steel"}`}
                    data-testid={`routing-save-stops-${draft.id}`}>
              {saving ? "Saving..." : "Save Edits"}
            </button>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-[12.5px] border border-coach-silver table-fixed">
        <thead className="bg-chrome-mist text-steel text-[10.5px] uppercase tracking-widest">
          <tr>
            <th className="w-10 py-1.5 px-2 text-left">#</th>
            <th className="w-32 py-1.5 px-2 text-left">Date</th>
            <th className="py-1.5 px-2 text-left">City</th>
            <th className="w-16 py-1.5 px-2 text-left">State</th>
            <th className="w-14 py-1.5 px-2"></th>
          </tr>
        </thead>
        <tbody>
          {(() => {
            let opIdx = 0;
            const items = [];
            rows.forEach((r, i) => {
            const needsReview = !r.stop_date || !r.city || !r.state;
            const isHomeBase = r.is_home_base === true;
            if (isHomeBase) {
              items.push(
                <tr key={i} className="border-t border-coach-silver/60 bg-chrome-mist"
                    data-testid="home-base-marker"
                    aria-readonly="true">
                  <td className="py-1.5 px-2 text-steel">
                    <span className="inline-block text-[9.5px] uppercase tracking-widest text-graphite bg-white border border-graphite/40 border-dashed rounded-sm px-1.5 py-[1px]">
                      HOME BASE
                    </span>
                  </td>
                  <td colSpan={3} className="py-1.5 px-2 text-carbon">
                    <div className="font-medium">{r.venue || "All Access Coach Leasing"}</div>
                    <div className="text-[11.5px] text-steel">{r.notes || "364 N Water Ave · Gallatin, TN 37066"}</div>
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <span className="text-steel text-[10.5px]">System</span>
                  </td>
                </tr>
              );
              return;
            }
            opIdx += 1;
            items.push(
              <tr key={i} className="border-t border-coach-silver/60"
                  data-testid={`routing-review-row-${draft.id}-${i}`}>
                <td className="py-1.5 px-2 text-steel">{opIdx}</td>
                <td className="py-1.5 px-2">
                  <input type="date" value={r.stop_date || ""} disabled={!canWrite}
                         onChange={(e) => setRow(i, { stop_date: e.target.value || null })}
                         className="border border-coach-silver rounded-sm px-1 py-0.5 text-[12.5px] w-full"
                         data-testid={`routing-review-date-${draft.id}-${i}`} />
                </td>
                <td className="py-1.5 px-2">
                  <input value={r.city || ""} disabled={!canWrite}
                         onChange={(e) => setRow(i, { city: e.target.value })}
                         className="border border-coach-silver rounded-sm px-1 py-0.5 text-[12.5px] w-full"
                         data-testid={`routing-review-city-${draft.id}-${i}`} />
                </td>
                <td className="py-1.5 px-2">
                  <input value={r.state || ""} disabled={!canWrite} maxLength={2}
                         onChange={(e) => setRow(i, { state: e.target.value.toUpperCase() })}
                         className="border border-coach-silver rounded-sm px-1 py-0.5 text-[12.5px] w-full"
                         data-testid={`routing-review-state-${draft.id}-${i}`} />
                </td>
                <td className="py-1.5 px-2 text-right">
                  {needsReview && <span className="text-amber-hold text-[10.5px] mr-2">Needs Review</span>}
                  {canWrite && (
                    <button onClick={() => removeRow(i)} className="text-critical-red text-[11.5px] hover:underline"
                            data-testid={`routing-review-remove-${draft.id}-${i}`}>Remove</button>
                  )}
                </td>
              </tr>
            );
          });
          if (rows.length === 0) {
            items.push(
              <tr key="empty"><td colSpan={5} className="py-4 text-center text-steel">No stops. Click <b>+ Add Stop</b> to begin.</td></tr>
            );
          }
          return items;
          })()}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function LegMileagePanel({ draft, canWrite, onChanged }) {
  const legs = draft.leg_mileage || [];
  const [busyIdx, setBusyIdx] = useState(null);
  const [manualDrafts, setManualDrafts] = useState({});

  const retry = async (idx, manual = null) => {
    setBusyIdx(idx);
    try {
      const body = manual != null ? { leg_index: idx, manual_miles: manual } : null;
      await routingApi.resolveMileage(draft.id, body);
      toast.success(manual != null ? "Leg mileage updated" : "Mileage recalculated");
      onChanged?.();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("resolve_mileage failed:", apiError(e));
      toast.error("Could not update mileage. Please try again.");
    } finally { setBusyIdx(null); }
  };

  const statusOf = (leg) => {
    if (leg?.status) return leg.status;
    if (leg?.manual_miles != null) return "manual";
    if (leg?.miles != null) return "estimated";
    return "unresolved";
  };
  const statusPill = (status) => {
    const map = {
      estimated: { bg: "bg-chrome-mist", text: "text-carbon", label: "Estimated" },
      manual: { bg: "bg-coach-blue/10", text: "text-coach-blue", label: "Manual" },
      unresolved: { bg: "bg-amber-hold/15", text: "text-amber-hold", label: "Unresolved" },
    };
    const s = map[status] || map.unresolved;
    return (
      <span className={`inline-block ${s.bg} ${s.text} text-[10px] uppercase tracking-widest px-1.5 py-[1px] rounded-sm border border-current/20`}>
        {s.label}
      </span>
    );
  };

  if (!legs.length) return null;

  // Owner-authorized 2026-02 · Mileage breakdown reconciliation.
  // Sum authoritative miles per leg_type. Uses manual_miles when
  // present, else miles. Unresolved legs contribute nothing to the
  // total but still surface via `unresolved > 0`.
  const legMiles = (l) =>
    l?.manual_miles != null ? Number(l.manual_miles)
    : l?.miles != null ? Number(l.miles) : 0;
  const sumOf = (t) =>
    legs.filter((l) => (l?.leg_type || "tour") === t).reduce((a, l) => a + legMiles(l), 0);
  const deadheadTo = sumOf("deadhead_to");
  const tourRouting = sumOf("tour");
  const deadheadFrom = sumOf("deadhead_from");
  const totalTourMileage = deadheadTo + tourRouting + deadheadFrom;
  const hasDeadheadTo = legs.some((l) => l.leg_type === "deadhead_to");
  const hasDeadheadFrom = legs.some((l) => l.leg_type === "deadhead_from");
  const statusForType = (t) => {
    const row = legs.find((l) => (l?.leg_type || "tour") === t);
    return row ? statusOf(row) : "estimated";
  };

  return (
    <div className="border border-coach-silver rounded-sm min-w-0 overflow-hidden" data-testid={`routing-legs-${draft.id}`}>
      {/* Mileage breakdown card — Home Base / Deadhead / Tour Routing / Total */}
      <div className="bg-road-white border-b border-coach-silver/60 p-3">
        <div className="text-[10.5px] uppercase tracking-widest text-steel mb-2">Mileage Breakdown</div>
        <div className="space-y-1 text-[12.5px] tabular">
          {hasDeadheadTo && (
            <div className="flex items-center justify-between" data-testid="mileage-breakdown-deadhead-to">
              <span className="text-carbon">Deadhead To</span>
              <span className="flex items-center gap-2">
                {statusPill(statusForType("deadhead_to"))}
                <span className="text-carbon min-w-[70px] text-right">{deadheadTo.toLocaleString()} mi</span>
              </span>
            </div>
          )}
          <div className="flex items-center justify-between" data-testid="tour-routing-mileage">
            <span className="text-carbon">Tour Routing</span>
            <span className="text-carbon min-w-[70px] text-right">{tourRouting.toLocaleString()} mi</span>
          </div>
          {hasDeadheadFrom && (
            <div className="flex items-center justify-between" data-testid="mileage-breakdown-deadhead-from">
              <span className="text-carbon">Deadhead From</span>
              <span className="flex items-center gap-2">
                {statusPill(statusForType("deadhead_from"))}
                <span className="text-carbon min-w-[70px] text-right">{deadheadFrom.toLocaleString()} mi</span>
              </span>
            </div>
          )}
          <div className="border-t border-coach-silver/60 pt-1 mt-1 flex items-center justify-between font-semibold">
            <span className="text-carbon uppercase tracking-widest text-[11px]">Total Tour Mileage</span>
            <span className="text-carbon min-w-[70px] text-right" data-testid="total-tour-mileage">
              {totalTourMileage.toLocaleString()} mi
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between p-2 border-b border-coach-silver/60">
        <div className="text-[11.5px] uppercase tracking-widest text-steel">Legs ({legs.length})</div>
        {canWrite && (
          <button onClick={() => retry(-1)} className="text-coach-blue text-[11.5px] hover:underline"
                  data-testid={`routing-legs-refresh-${draft.id}`}>Recompute All</button>
        )}
      </div>
      <div className="overflow-x-auto">
      {/* T6 mobile fix · table-fixed + responsive column widths + mobile-stacked
          action cell so that all cells collapse within 375px viewport without
          horizontal scroll or badge-cell overflow. Desktop layout unchanged
          at sm+ (≥640px). */}
      <table className="w-full text-[12px] table-fixed">
        <tbody>
          {legs.map((leg, i) => {
            const st = statusOf(leg);
            const showMiles = leg.manual_miles != null ? leg.manual_miles : leg.miles;
            const lt = leg.leg_type || "tour";
            const isDeadhead = lt !== "tour";
            const dhLabel = lt === "deadhead_to" ? "DEADHEAD TO"
                          : lt === "deadhead_from" ? "DEADHEAD FROM" : null;
            const dhTestId = lt === "deadhead_to" ? "deadhead-to-leg"
                           : lt === "deadhead_from" ? "deadhead-from-leg" : undefined;
            return (
              <tr key={i} className={`border-t border-coach-silver/60 ${isDeadhead ? "bg-chrome-mist/40" : ""}`}
                  data-testid={dhTestId || `routing-leg-${draft.id}-${i}`}>
                <td className="py-1.5 px-2 text-carbon break-words align-top">
                  {dhLabel && (
                    <div className="mb-1 flex flex-wrap items-center gap-1">
                      <span className="inline-block text-[9.5px] uppercase tracking-widest text-carbon bg-coach-silver rounded-sm px-1.5 py-[1px] whitespace-nowrap">
                        {dhLabel}
                      </span>
                      <span className="inline-block text-[9.5px] uppercase tracking-widest text-white bg-steel rounded-sm px-1.5 py-[1px] whitespace-nowrap">
                        HOME BASE
                      </span>
                    </div>
                  )}
                  <div className="break-words">
                    {leg.from_city || "—"}{leg.from_state ? `, ${leg.from_state}` : ""}
                    <span className="text-steel mx-2">→</span>
                    {leg.to_city || "—"}{leg.to_state ? `, ${leg.to_state}` : ""}
                  </div>
                </td>
                <td className="py-1.5 px-2 w-[76px] sm:w-24 align-top" data-testid={`routing-leg-status-${draft.id}-${i}`}>
                  {statusPill(st)}
                </td>
                <td className="py-1.5 px-2 text-right w-14 sm:w-24 align-top">
                  {st === "unresolved" ? (
                    <span className="text-amber-hold text-[11px]">—</span>
                  ) : (
                    <span data-testid={`routing-leg-miles-${draft.id}-${i}`}>
                      {showMiles} mi
                    </span>
                  )}
                </td>
                {canWrite && (
                  <td className="py-1.5 px-2 text-right w-[92px] sm:w-48 align-top">
                    <div className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center gap-1">
                      <input type="number" min={0} placeholder="mi"
                             value={manualDrafts[i] ?? ""}
                             onChange={(e) => setManualDrafts({ ...manualDrafts, [i]: e.target.value })}
                             className="border border-coach-silver rounded-sm px-1 py-0.5 text-[12px] w-full sm:w-16"
                             data-testid={`routing-leg-manual-input-${draft.id}-${i}`} />
                      <button disabled={busyIdx === i}
                              onClick={() => retry(i, manualDrafts[i] || null)}
                              className="border border-coach-blue text-coach-blue text-[11.5px] px-1.5 py-0.5 rounded-sm"
                              data-testid={`routing-leg-retry-${draft.id}-${i}`}>
                        {manualDrafts[i] ? "Save" : "Retry"}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function CoDriverPanel({ draft }) {
  const rec = draft?.co_driver_recommendation;
  const [showLegs, setShowLegs] = useState(false);
  if (!rec || !rec.co_driver_recommended) return null;
  const legs = rec.flagged_legs || [];
  return (
    <div className="border border-coach-blue bg-coach-blue/5 rounded-sm p-3"
         data-testid={`routing-codriver-${draft.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[13px] text-carbon font-medium" data-testid={`routing-codriver-title-${draft.id}`}>
          Co-Driver Review Recommended
        </div>
        <button type="button" onClick={() => setShowLegs((v) => !v)}
                className="text-coach-blue text-[11.5px] hover:underline"
                data-testid={`routing-codriver-toggle-${draft.id}`}>
          {showLegs ? "Hide legs" : "Show legs"}
        </button>
      </div>
      <div className="text-[12px] text-steel mt-0.5"
           data-testid={`routing-codriver-summary-${draft.id}`}>
        {rec.flagged_leg_count} leg{rec.flagged_leg_count === 1 ? "" : "s"} exceed single-driver planning limits.
        {" "}Suggested Co-Driver days: <strong className="text-carbon">{rec.recommended_co_driver_days}</strong>
      </div>
      {showLegs && (
        <ul className="mt-2 text-[12px] text-carbon list-disc pl-4 space-y-0.5">
          {legs.map((l, i) => (
            <li key={i} data-testid={`routing-codriver-leg-${draft.id}-${i}`}>
              {l.from_city} → {l.to_city}: {l.miles.toLocaleString()} mi over {l.travel_days} day{l.travel_days === 1 ? "" : "s"}
              {" "}({l.effective_daily_miles} mi/day · ~{l.estimated_drive_hours_per_day}h/day)
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TimelineComponent({ stops }) {
  if (!stops || stops.length === 0) return <div className="text-steel text-[13px]">No stops parsed.</div>;
  return (
    <ol className="relative border-l-2 border-coach-silver ml-3" data-testid="routing-timeline">
      {stops.map((s, i) => (
        <li key={i} className="ml-6 pb-4 relative" data-testid={`timeline-stop-${i}`}>
          <span className="absolute -left-[34px] top-0 w-4 h-4 rounded-full bg-coach-blue border-2 border-white shadow" />
          <div className="text-[13.5px] text-carbon font-medium">
            {s.city}{s.state ? `, ${s.state}` : ""}
          </div>
          <div className="text-[11.5px] text-steel">
            {s.stop_date || "date TBD"}
            {s.venue && <> · {s.venue}</>}
          </div>
        </li>
      ))}
    </ol>
  );
}
